/**
 * Wallet Settings — Accessibility + Language + Support preferences.
 * Every change fires a signal.
 */
import { WalletShell, useWalletActive, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { WALLET_LANGUAGES } from "@/components/wallet/LanguageSwitcher";
import { useAccessibility } from "@/contexts/AccessibilityContext";
import WalletAppSettingsCard from "@/components/wallet/WalletAppSettingsCard";
import { Settings as SettingsIcon, Languages, Eye, Volume2, Contrast, Zap, MessageCircle, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const LANGUAGES = WALLET_LANGUAGES.map(l => [l.code, l.native]);

const FONT_SIZES = [
  ["sm", "Small", "text-xs"], ["base", "Default", "text-sm"],
  ["lg", "Large", "text-base"], ["xl", "Extra large", "text-lg"],
];

const CHANNELS = [
  ["whatsapp", "WhatsApp"], ["sms", "SMS"], ["email", "Email"],
  ["phone", "Phone call"], ["branch", "In branch"], ["in_app", "In this app only"],
];

const WINDOWS = [
  ["business_hours", "Business hours"],
  ["evenings", "Evenings"],
  ["weekends", "Weekends"],
  ["any", "Any time"],
];

export default function WalletSettings() {
  const { prefs, update } = useAccessibility();
  const active = useWalletActive();

  return (
    <WalletShell activeTab={active}>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="wallet-settings">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Settings</div>
          <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>Make your wallet yours</h1>
          <p className="text-sm text-slate-600 mt-1">All preferences stay on this device. NAVI adapts automatically.</p>
        </header>

        {/* Language */}
        <Section icon={Languages} title="Language">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-no-translate="true">
            {LANGUAGES.map(([code, label]) => (
              <button
                key={code}
                onClick={() => update({ language: code })}
                data-testid={`settings-lang-${code}`}
                data-no-translate="true"
                className="px-3 py-2 rounded-xl border text-sm transition"
                style={{
                  background: prefs.language === code ? NAVY : "white",
                  color: prefs.language === code ? "white" : "#334155",
                  borderColor: prefs.language === code ? NAVY : "#E2E8F0",
                }}
              >{label}</button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">NAVI answers + document summaries can be translated to your chosen language on any screen.</p>
        </Section>

        {/* Text size */}
        <Section icon={Eye} title="Text size">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {FONT_SIZES.map(([code, label, klass]) => (
              <button
                key={code}
                onClick={() => update({ font_size: code })}
                data-testid={`settings-font-${code}`}
                className={`px-3 py-2 rounded-xl border transition ${klass}`}
                style={{
                  background: prefs.font_size === code ? NAVY : "white",
                  color: prefs.font_size === code ? "white" : "#334155",
                  borderColor: prefs.font_size === code ? NAVY : "#E2E8F0",
                }}
              >{label}</button>
            ))}
          </div>
        </Section>

        {/* Contrast + reduced motion */}
        <Section icon={Contrast} title="Vision & motion">
          <ToggleRow
            testId="settings-contrast"
            active={prefs.contrast === "high"}
            onToggle={v => update({ contrast: v ? "high" : "normal" })}
            label="High contrast"
            hint="Darker text, thicker borders."
          />
          <ToggleRow
            testId="settings-motion"
            active={prefs.reduce_motion}
            onToggle={v => update({ reduce_motion: v })}
            label="Reduce motion"
            hint="Fewer animations and transitions."
            icon={Zap}
          />
        </Section>

        {/* Support preferences */}
        <Section icon={MessageCircle} title="How your bank can contact you">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Preferred channel</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {CHANNELS.map(([code, label]) => (
              <button
                key={code}
                onClick={() => update({ support_channel: code })}
                data-testid={`settings-channel-${code}`}
                className="px-3 py-2 rounded-xl border text-sm transition"
                style={{
                  background: prefs.support_channel === code ? NAVY : "white",
                  color: prefs.support_channel === code ? "white" : "#334155",
                  borderColor: prefs.support_channel === code ? NAVY : "#E2E8F0",
                }}
              >{label}</button>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-4 mb-1">Contact window</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {WINDOWS.map(([code, label]) => (
              <button
                key={code}
                onClick={() => update({ contact_window: code })}
                data-testid={`settings-window-${code}`}
                className="px-3 py-2 rounded-xl border text-sm transition"
                style={{
                  background: prefs.contact_window === code ? NAVY : "white",
                  color: prefs.contact_window === code ? "white" : "#334155",
                  borderColor: prefs.contact_window === code ? NAVY : "#E2E8F0",
                }}
              >{label}</button>
            ))}
          </div>
          <ToggleRow
            testId="settings-proactive"
            active={prefs.proactive}
            onToggle={v => update({ proactive: v })}
            label="Proactive check-ins"
            hint="Let your bank send a follow-up 90 days after a review."
          />
        </Section>

        {/* App & offline */}
        <WalletAppSettingsCard />

        {/* My Identity */}
        <Link
          to="/wallet/identity"
          data-testid="settings-identity-link"
          className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 flex items-center justify-between hover:shadow-md transition"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}22` }}>
              <ShieldCheck className="w-4 h-4" style={{ color: NAVY }} />
            </div>
            <div>
              <div className="text-sm font-semibold" style={{ color: NAVY }}>My Identity</div>
              <div className="text-[11px] text-slate-500">Enter your identity once. Reuse it across every institution.</div>
            </div>
          </div>
          <span className="text-xs" style={{ color: NAVY }}>→</span>
        </Link>

        {/* My Consent */}
        <Link
          to="/wallet/consent"
          data-testid="settings-consent-link"
          className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 flex items-center justify-between hover:shadow-md transition"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}22` }}>
              <ShieldCheck className="w-4 h-4" style={{ color: NAVY }} />
            </div>
            <div>
              <div className="text-sm font-semibold" style={{ color: NAVY }}>My Consent</div>
              <div className="text-[11px] text-slate-500">Institutions asking for your data. Grant, decline, or revoke — and see who has looked.</div>
            </div>
          </div>
          <span className="text-xs" style={{ color: NAVY }}>→</span>
        </Link>

        {/* Provenance link */}
        <Link to="/wallet/provenance" data-testid="settings-provenance-link" className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 flex items-center justify-between hover:shadow-md transition">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}22` }}>
              <ShieldCheck className="w-4 h-4" style={{ color: NAVY }} />
            </div>
            <div>
              <div className="text-sm font-semibold" style={{ color: NAVY }}>What Altara knows about me</div>
              <div className="text-[11px] text-slate-500">Every signal we&rsquo;ve stored, revocable per category.</div>
            </div>
          </div>
          <span className="text-xs" style={{ color: NAVY }}>→</span>
        </Link>
      </div>
    </WalletShell>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: GOLD }} />
        <div className="text-sm font-semibold" style={{ color: NAVY }}>{title}</div>
      </div>
      {children}
    </section>
  );
}

function ToggleRow({ testId, active, onToggle, label, hint }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="pr-3">
        <div className="text-sm text-slate-800">{label}</div>
        {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
      </div>
      <button
        data-testid={testId}
        onClick={() => onToggle(!active)}
        className="w-10 h-6 rounded-full relative transition"
        style={{ background: active ? NAVY : "#CBD5E1" }}
      >
        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition" style={{ left: active ? "18px" : "2px" }} />
      </button>
    </div>
  );
}
